import { useMemo, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { format, addDays, subDays, isSameDay } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { HoverCard } from '@/components/ui/HoverCard';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { formatLongDateLocalized } from '@/utils/dateFormat';
import { Post } from '@/services/posts';
import { CalendarNote } from '@/types/calendar-notes';
import { filterCampaignsForDay } from '@/utils/campaignMatcher';
import { useCalendarPosts, useMultiCalendarPosts } from '@/hooks/useCalendarPosts';
import { useCalendarNotes } from '@/hooks/useCalendarNotes';
import { useCalendarHolidays } from '@/hooks/useCalendarHolidays';
import { useTimezone } from '@/contexts/TimezoneContext';
import { SquarePostCard } from './CalendarDayPosts';
import { CalendarNavigation } from './CalendarNavigation';
import { CalendarView } from '@/types/calendar';
import { getContrastColor } from '@/utils/colors';
import { useCalendarSettingsStore } from '@/stores/calendarSettingsStore';
import { RetryImage } from '@/components/ui';
import { PlaceholderImage } from '@/components/ui/PlaceholderImage';
import { MoonPhaseIndicator } from './MoonPhase';
import { QuickAddMenu } from './QuickAddMenu';
import { getInferredStatus, getStatusIcon } from '@/utils/calendarStatus';
import { CalendarPostPopover } from './PostPopover';
import { recordSlowOp } from '@/utils/perfTelemetry';

/**
 * CalendarDayView Component
 *
 * Hobonichi-style day view with a conditional overnight block and three daytime blocks.
 * Clean, generous spacing, content-first design.
 */

interface CalendarDayViewProps {
  /**
   * Current view mode
   */
  view: CalendarView;

  /**
   * Callback when view changes
   */
  onViewChange: (view: CalendarView) => void;

  /**
   * Day to display
   */
  currentDate: Date;

  /**
   * Navigate to different day
   */
  onDateChange: (date: Date) => void;

  /**
   * Click on post (optional) - for drawer
   */
  onDateSelect: (date: Date, posts: Post[]) => void;

  /**
   * Edit a post directly (opens PostCreator modal)
   * If calendarMappingId is provided, opens in shooting mode
   */
  onEditPost?: (postId: number, calendarMappingId?: number) => void;

  /**
   * Delete/unmap a post from the calendar
   */
  onPostDelete?: (post: Post) => void;

  /**
   * Calendar IDs for multi-calendar support
   */
  calendarIds: number[];

  /**
   * Platform filter
   */
  platform?: string;

  /**
   * Global drag state
   */
  isDraggingActive?: boolean;

  /**
   * Drag-drop handler
   */
  onPostRescheduled?: (postId: number, newDate: Date) => void;

  /**
   * Click on campaign to edit
   */
  onCampaignClick?: (campaign: CalendarNote) => void;

  /**
   * Callback when add post button is clicked
   */
  onAddPost?: (date: Date) => void;

  /**
   * Callback when add campaign button is clicked
   */
  onAddCampaign?: (date: Date) => void;

  /**
   * Callback when add content pillar button is clicked
   */
  onAddContentPillar?: (date: Date) => void;
}

interface TimeBlock {
  id: string;
  label: string;
  subtitle?: string;
  hours: [number, number];
  icon?: string;
}

// TimeBlocks are defined as a function to use translation hook
const useTimeBlocks = (): TimeBlock[] => {
  const { t } = useTranslation('common');

  return useMemo(() => [
    {
      id: 'night',
      label: t('night'),
      subtitle: t('night_time_range'),
      hours: [0, 6] as [number, number],
      icon: ICONS.WEATHER_NIGHT_CLOUD,
    },
    {
      id: 'morning',
      label: t('morning'),
      subtitle: t('morning_time_range'),
      hours: [6, 12] as [number, number],
      icon: ICONS.BULB, // Represents sunrise/brightness
    },
    {
      id: 'afternoon',
      label: t('afternoon'),
      subtitle: t('afternoon_time_range'),
      hours: [12, 18] as [number, number],
      icon: ICONS.CLOUD,
    },
    {
      id: 'evening',
      label: t('evening'),
      subtitle: t('evening_time_range'),
      hours: [18, 24] as [number, number],
      icon: ICONS.STAR, // Represents night/stars
    },
  ], [t]);
};

// Generate all 24 hourly slots; overnight slots appear when they contain posts.
const useHourlyBlocks = (): TimeBlock[] => {
  return useMemo(() => {
    const blocks: TimeBlock[] = [];
    for (let hour = 0; hour < 24; hour++) {
      blocks.push({
        id: `hour-${hour}`,
        label: `${hour.toString().padStart(2, '0')}:00`,
        hours: [hour, hour + 1] as [number, number],
      });
    }
    return blocks;
  }, []);
};

// Compact Post Card for hourly view (medium size with popover, draggable)
const CompactPostCard = ({
  post,
  date,
  onClick,
  onDelete,
  isDraggingGlobal = false,
}: {
  post: Post;
  date: Date;
  onClick: () => void;
  onDelete: (post: Post) => void;
  isDraggingGlobal?: boolean;
}) => {
  const navigate = useNavigate();
  const isBlog = post.source === 'blog';
  const firstMedia = post.postMedia?.[0];
  const hasMedia = firstMedia && (firstMedia.thumbnailUrl || firstMedia.originalUrl);
  const statusIcon = getStatusIcon(post);
  const inferredStatus = getInferredStatus(post);

  // Allow dragging for MAPPED (not yet committed) and SCHEDULED (committed but not yet published)
  // In-flight (PUBLISHING) and completed (PUBLISHED, FAILED) posts cannot be moved
  // Blog entries are READ-ONLY: never draggable
  const canDrag = !isBlog && (inferredStatus === 'MAPPED' || inferredStatus === 'SCHEDULED');

  // mappingId is set per-entry from calendar endpoint, ensuring uniqueness for multi-mapped posts
  const mappingId = post.mappingId;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    // Use mappingId in ID to ensure uniqueness when same post has multiple mappings.
    // Blog entries use a namespaced id so they never collide with a real social post's id.
    id: isBlog ? `blog-post-${post.blogPostId}` : `calendar-post-${mappingId || post.id}`,
    data: {
      type: 'calendar-post',
      post,
      mappingId, // Include specific mappingId for move operations
      sourceDate: date.toISOString(),
    },
    disabled: !canDrag || isBlog,
  });

  // Blog chips click straight through to the blog post, bypassing the edit flow
  const handleClick = () => {
    if (isBlog) {
      navigate(`/blog/${post.blogPostId}`);
      return;
    }
    onClick();
  };

  return (
    <HoverCard
      trigger={
        <div
          ref={setNodeRef}
          {...(canDrag ? { ...listeners, ...attributes } : {})}
          onClick={handleClick}
          className={cn(
            "relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 shadow-sm transition-all group",
            canDrag && !isDragging && "cursor-grab hover:ring-2 hover:ring-primary-400 hover:shadow-md",
            !canDrag && "cursor-pointer hover:ring-2 hover:ring-primary-400 hover:shadow-md",
            isDragging && "opacity-50 cursor-grabbing",
            isDraggingGlobal && !isDragging && "opacity-70"
          )}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleClick();
            }
          }}
        >
          {hasMedia ? (
            <RetryImage
              src={firstMedia.thumbnailUrl || firstMedia.originalUrl || undefined}
              alt=""
              className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <PlaceholderImage />
          )}
          {/* Blog badge (top-right) — distinguishes read-only WordPress blog chips from social posts */}
          {isBlog && (
            <div className="absolute top-0.5 right-0.5 w-4 h-4 flex items-center justify-center bg-white/90 rounded">
              <Icon name={ICONS.BLOG} size="xs" className="text-gray-600" ariaHidden />
            </div>
          )}
          {/* Bottom bar with status icon */}
          <div className="absolute bottom-0 inset-x-0 bg-black/70 py-0.5 flex justify-center">
            <Icon name={statusIcon.icon} size="xs" className="text-white" ariaHidden />
          </div>
        </div>
      }
      side="left"
      align="start"
      sideOffset={8}
    >
      <CalendarPostPopover post={post} inferredStatus={inferredStatus} onEdit={() => onClick()} onDelete={onDelete} />
    </HoverCard>
  );
};

/**
 * Filter posts by time block (timezone-aware)
 * @param posts - Posts to filter
 * @param block - Time block definition
 * @param getHourFn - Function to get hour in user's timezone
 */
const getPostsForBlock = (
  posts: Post[],
  block: TimeBlock,
  getHourFn: (date: string | Date | null | undefined) => number | null
): Post[] => {
  return posts.filter((post) => {
    if (!post.scheduledAt) return false;
    const hour = getHourFn(post.scheduledAt);
    if (hour === null) return false;
    return hour >= block.hours[0] && hour < block.hours[1];
  });
};

/**
 * Time Block Component
 */
interface TimeBlockSectionProps {
  block: TimeBlock;
  posts: Post[];
  date: Date;
  isDraggingActive: boolean;
  onEdit: (post: Post) => void;
  onDelete: (post: Post) => void;
  compact?: boolean; // For hourly view with smaller sections
  onAddPost?: (date: Date) => void;
  onAddCampaign?: (date: Date) => void;
  onAddContentPillar?: (date: Date) => void;
}

const TimeBlockSection = ({
  block,
  posts,
  date,
  isDraggingActive,
  onEdit,
  onDelete,
  compact = false,
  onAddPost,
  onAddCampaign,
  onAddContentPillar,
}: TimeBlockSectionProps) => {
  const { t } = useTranslation('common');
  const { isOver, setNodeRef } = useDroppable({
    id: `day-block-${format(date, 'yyyy-MM-dd')}-${block.id}`,
    data: {
      type: 'calendar-time-block',
      date: date.toISOString(),
      block: block.id,
    },
  });

  const emptyLabel =
    block.id === 'morning'
      ? t('no_posts_for_timeblock_morning')
      : block.id === 'afternoon'
        ? t('no_posts_for_timeblock_afternoon')
        : block.id === 'evening'
          ? t('no_posts_for_timeblock_evening')
          : t('no_posts_for_timeblock', { block: block.label });

  // Compact hourly view
  if (compact) {
    return (
      <div className="flex border-b border-gray-100 last:border-b-0 relative">
        {/* Hour label */}
        <div className="w-8 lg:w-20 flex-shrink-0 px-0.5 lg:px-3 py-2 bg-gray-50 border-r border-gray-100 flex flex-col items-center justify-center gap-1 group">
          <span className="text-[9px] lg:text-xs font-medium text-gray-600 leading-tight">{block.label}</span>
          <QuickAddMenu
            date={date}
            onAddPost={onAddPost}
            onAddCampaign={onAddCampaign}
            onAddContentPillar={onAddContentPillar}
            menuPosition="left"
            showOnHover={true}
          />
        </div>

        {/* Posts Container */}
        <div
          ref={setNodeRef}
          className={cn(
            'flex-1 px-3 py-1 transition-all flex items-center',
            posts.length === 0 ? 'min-h-[16px] lg:min-h-[40px]' : 'min-h-[28px] lg:min-h-[40px]',
            isOver && 'bg-primary-50',
            !isOver && 'bg-white hover:bg-gray-50'
          )}
        >
          {posts.length === 0 ? (
            <div className="h-full flex items-center">
              {isOver && <span className="text-xs text-primary-600">{t('drop_here')}</span>}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {posts.map((post) => (
                <CompactPostCard
                  key={post.source === 'blog' ? `blog-${post.blogPostId}` : `${post.id}-${post.mappingId || 'unmapped'}`}
                  post={post}
                  date={date}
                  onClick={() => onEdit(post)}
                  onDelete={onDelete}
                  isDraggingGlobal={isDraggingActive}
                />
              ))}
            </div>
          )}
        </div>

        {/* Drop indicator */}
        {isOver && (
          <div className="absolute inset-0 border-2 border-dashed border-primary-500 bg-primary-50/30 pointer-events-none" />
        )}
      </div>
    );
  }

  // Full block view (Night when used, then Morning/Afternoon/Evening)
  return (
    <div className="border-b border-gray-200 last:border-b-0 relative">
      {/* Block Header */}
      <div className="bg-gray-50 px-4 sm:px-6 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2 group">
          {block.icon && <Icon name={block.icon} size="sm" color="text-gray-600" ariaHidden />}
          <h3 className="text-sm font-semibold text-gray-900">{block.label}</h3>
          {block.subtitle && <p className="text-xs text-gray-500">{block.subtitle}</p>}
          {posts.length > 0 && (
            <span className="ml-auto text-xs text-gray-500 bg-white px-2 py-1 rounded-full">
              {t('x_posts', { count: posts.length })}
            </span>
          )}
          <div className={posts.length === 0 ? 'ml-auto' : ''}>
            <QuickAddMenu
              date={date}
              onAddPost={onAddPost}
              onAddCampaign={onAddCampaign}
              onAddContentPillar={onAddContentPillar}
              menuPosition="right"
              showOnHover={true}
            />
          </div>
        </div>
      </div>

      {/* Posts Container */}
      <div
        ref={setNodeRef}
        className={cn(
          'px-4 sm:px-6 py-3 min-h-[60px] transition-all',
          posts.length === 0 && 'max-lg:py-2 max-lg:min-h-[44px]',
          isOver && 'bg-primary-50 border-2 border-dashed border-primary-500',
          !isOver && 'bg-white'
        )}
      >
        {posts.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-1 lg:py-3">
            {block.icon && (
              <Icon
                name={block.icon}
                size="sm"
                color="text-gray-300"
                ariaHidden
              />
            )}
            <p className="text-xs lg:text-sm text-gray-400">
              {isDraggingActive ? t('drop_posts_here') : emptyLabel}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 lg:gap-3">
            {posts.map((post) => (
              <SquarePostCard
                key={post.source === 'blog' ? `blog-${post.blogPostId}` : `${post.id}-${post.mappingId || 'unmapped'}`}
                post={post}
                date={date}
                onEdit={() => onEdit(post)}
                onDelete={onDelete}
                isDragging={isDraggingActive}
              />
            ))}
          </div>
        )}
      </div>

      {/* Drop zone indicator overlay */}
      {isOver && (
        <div className="absolute inset-0 border-2 border-dashed border-primary-500 bg-primary-50/50 pointer-events-none" />
      )}
    </div>
  );
};

export const CalendarDayView = ({
  view,
  onViewChange,
  currentDate,
  onDateChange,
  onDateSelect,
  onEditPost,
  onPostDelete,
  calendarIds,
  platform,
  isDraggingActive = false,
  onCampaignClick,
  onAddPost,
  onAddCampaign,
  onAddContentPillar,
}: CalendarDayViewProps) => {
  const __renderStart = performance.now();
  useLayoutEffect(() => {
    recordSlowOp('render:CalendarDayView', performance.now() - __renderStart);
  });
  const { t } = useTranslation('common');
  const { getHourInTimezone, timezone } = useTimezone();
  const { timeFormat, showMoonPhases } = useCalendarSettingsStore();
  const TIME_BLOCKS = useTimeBlocks();
  const HOURLY_BLOCKS = useHourlyBlocks();

  // "today" anchored at noon in team timezone so isSameDay comparisons are tz-correct
  const zonedNow = toZonedTime(new Date(), timezone);
  const today = fromZonedTime(
    `${zonedNow.getFullYear()}-${String(zonedNow.getMonth() + 1).padStart(2, '0')}-${String(zonedNow.getDate()).padStart(2, '0')}T12:00:00`,
    timezone
  );
  const isToday = isSameDay(currentDate, today);
  const isHourlyView = timeFormat === 'hourly';

  // Fetch posts for the current month
  const monthString = format(currentDate, 'yyyy-MM');
  const shouldUseMultiCalendar = calendarIds && calendarIds.length > 0;

  const multiCalendarResult = useMultiCalendarPosts({
    month: monthString,
    calendarIds: calendarIds || [],
    platform,
    enabled: shouldUseMultiCalendar,
  });

  const singleCalendarResult = useCalendarPosts({
    month: monthString,
    platform,
    enabled: !shouldUseMultiCalendar,
  });

  const { isLoading, getPostsForDate, refetch } = shouldUseMultiCalendar
    ? multiCalendarResult
    : singleCalendarResult;

  // Fetch campaigns for the current month (only if a calendar is selected)
  const startOfMonthDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const endOfMonthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const hasCalendarSelected = calendarIds && calendarIds.length > 0;
  const { data: notesData } = useCalendarNotes(
    hasCalendarSelected ? calendarIds[0] : -1, // -1 will be caught by enabled check in hook
    format(startOfMonthDate, 'yyyy-MM-dd'),
    format(endOfMonthDate, 'yyyy-MM-dd')
  );

  // Fetch holidays for the current month
  const { data: holidaysData } = useCalendarHolidays(
    hasCalendarSelected ? calendarIds[0] : -1,
    format(startOfMonthDate, 'yyyy-MM-dd'),
    format(endOfMonthDate, 'yyyy-MM-dd')
  );

  // Get posts for current day
  const dateString = format(currentDate, 'yyyy-MM-dd');
  const dayPosts = getPostsForDate(dateString);

  // Filter campaigns for current day (handles recurring campaigns)
  const dayCampaigns = useMemo(() => {
    return filterCampaignsForDay(notesData, currentDate);
  }, [notesData, currentDate]);

  // Filter holidays for current day
  const dayHolidays = useMemo(() => {
    if (!holidaysData) return [];
    return holidaysData.filter((holiday) => {
      const holidayDate = new Date(holiday.holidayDate);
      return isSameDay(holidayDate, currentDate);
    });
  }, [holidaysData, currentDate]);

  // Group posts by time block (timezone-aware)
  // Derive local hours from each UTC instant. On the autumn clock change, both
  // occurrences of 02:00 remain visible together in the 02:00 slot.
  const postsByBlock = useMemo(() => {
    const blocks = isHourlyView ? HOURLY_BLOCKS : TIME_BLOCKS;
    const grouped = blocks.map((block) => ({
      block,
      posts: getPostsForBlock(dayPosts, block, getHourInTimezone),
    }));
    const hasOvernightPosts = grouped.some(
      ({ block, posts }) => block.hours[0] < 6 && posts.length > 0
    );
    // Show only occupied overnight hours; retain the complete Night block
    // once any overnight post exists. Ordinary days keep their current density.
    return grouped.filter(({ block, posts }) =>
      block.hours[0] >= 6 || (isHourlyView ? posts.length > 0 : hasOvernightPosts)
    );
  }, [dayPosts, getHourInTimezone, isHourlyView, HOURLY_BLOCKS, TIME_BLOCKS]);

  // Navigation
  const handlePrevDay = () => {
    onDateChange(subDays(currentDate, 1));
  };

  const handleNextDay = () => {
    onDateChange(addDays(currentDate, 1));
  };

  const handleToday = () => {
    onDateChange(new Date());
  };

  const handleEdit = (post: Post) => {
    if (post.source === 'blog') return;
    // Carry the clicked occurrence's mapping through to the composer.
    if (onEditPost) onEditPost(post.id, post.mappingId);
    else onDateSelect(currentDate, [post]);
  };

  const handleDelete = (post: Post) => {
    if (post.source !== 'blog') onPostDelete?.(post);
  };

  // Format title with "Today" prefix if applicable
  // Short format for mobile, full format for desktop
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const dateLabel = formatLongDateLocalized(currentDate, isMobile);
  const title = isToday ? `${t('today')} · ${dateLabel}` : dateLabel;

  return (
    <div className="flex flex-col bg-white" data-calendar-day={format(currentDate, 'yyyy-MM-dd')}>
      {/* Unified Calendar Navigation */}
      <CalendarNavigation
        view={view}
        onViewChange={onViewChange}
        currentDate={currentDate}
        onPrev={handlePrevDay}
        onNext={handleNextDay}
        onToday={handleToday}
        title={title}
        hideTodayButton={isToday}
        onRefresh={refetch}
        isLoading={isLoading}
      />

      {/* Stats Bar — desktop only */}
      <div className="hidden lg:block px-4 sm:px-6 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-3">
            <span className="text-gray-600">
              {t('x_posts_scheduled', { count: dayPosts.length })}
            </span>
            {showMoonPhases && (
              <MoonPhaseIndicator date={currentDate} size="sm" showLabel className="text-gray-500" />
            )}
          </div>
          {/* Only show block breakdown for blocks view, not hourly */}
          {!isHourlyView && dayPosts.length > 0 && (
            <div className="flex items-center gap-4">
              {postsByBlock.map(({ block, posts }) => (
                posts.length > 0 && block.icon && (
                  <div key={block.id} className="flex items-center gap-1">
                    <Icon name={block.icon} size="xs" color="text-gray-400" ariaHidden />
                    <span className="text-gray-500 text-xs">
                      {block.label}: {posts.length}
                    </span>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Campaigns and Holidays for this day */}
      {(dayCampaigns.length > 0 || dayHolidays.length > 0) && (
        <div className="px-4 sm:px-6 py-3 border-b border-gray-200 space-y-2">
          {/* Campaign badges */}
          {dayCampaigns.map((campaign) => {
            const campaignColor = (campaign.color && campaign.color.trim()) || '#3B82F6';
            return (
              <Tooltip
                key={campaign.id}
                content={
                  <div>
                    <div>
                      <span className="font-medium">{campaign.repeatType ? t('content_pillar', { defaultValue: 'Content Pillar' }) : t('campaign')}:</span> {campaign.title}
                      {campaign.repeatType && (
                        <span className="text-gray-300 ml-1">
                          ({t(`repeat_${campaign.repeatType.toLowerCase()}`)})
                        </span>
                      )}
                    </div>
                    {campaign.description && (
                      <div className="text-gray-300 text-xs mt-1">{campaign.description}</div>
                    )}
                  </div>
                }
              >
                <div
                  onClick={() => onCampaignClick?.(campaign)}
                  className={cn(
                    'px-3 py-2 rounded-lg font-medium text-sm',
                    onCampaignClick && 'cursor-pointer hover:opacity-90 transition-opacity'
                  )}
                  style={{
                    backgroundColor: campaignColor,
                    color: getContrastColor(campaignColor),
                  }}
                >
                  {campaign.title}
                  {campaign.description && (
                    <p className="text-xs mt-0.5 opacity-90">{campaign.description}</p>
                  )}
                </div>
              </Tooltip>
            );
          })}

          {/* Holiday badges */}
          {dayHolidays.map((holiday) => {
            const holidayColor = holiday.colorCode || '#FFD700';
            return (
              <Tooltip
                key={holiday.id}
                content={
                  <div>
                    <span className="font-medium">{t('holiday')}:</span> {holiday.localName}
                    {holiday.countryName && (
                      <span className="text-gray-300 ml-1">({holiday.countryName})</span>
                    )}
                  </div>
                }
              >
                <div
                  className="px-3 py-2 rounded-lg font-medium text-sm"
                  style={{
                    backgroundColor: holidayColor,
                    color: getContrastColor(holidayColor),
                  }}
                >
                  {holiday.localName}
                </div>
              </Tooltip>
            );
          })}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-2 text-gray-600">
            <Icon name={ICONS.LOADER} size="md" className="animate-spin" ariaHidden />
            <span>{t('loading_day')}</span>
          </div>
        </div>
      )}

      {/* Time Blocks / Hourly Slots */}
      {!isLoading && (
        <div className="overflow-y-auto">
          {postsByBlock.map(({ block, posts }) => (
            <TimeBlockSection
              key={block.id}
              block={block}
              posts={posts}
              date={currentDate}
              isDraggingActive={isDraggingActive}
              onEdit={handleEdit}
              onDelete={handleDelete}
              compact={isHourlyView}
              onAddPost={onAddPost}
              onAddCampaign={onAddCampaign}
              onAddContentPillar={onAddContentPillar}
            />
          ))}
        </div>
      )}
    </div>
  );
};
